import { useUser } from '@folklore/auth';
import { loadFacebook } from '@folklore/services';
import { createContext, ReactNode, use, useEffect, useState } from 'react';

import { useIsCheckingAuth } from './AuthContext';

interface FacebookContextValue {
    appId: string | null;
    sdk: facebook.FacebookStatic | null;
    token: string | null;
    setRequiresSdk: (requiresSdk: boolean) => void;
}

const getWindowFacebook = (): facebook.FacebookStatic | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    return typeof (window as Window & { FB?: facebook.FacebookStatic }).FB !== 'undefined'
        ? (window as Window & { FB?: facebook.FacebookStatic }).FB || null
        : null;
};

export const FacebookContext = createContext<FacebookContextValue>({
    appId: null,
    sdk: getWindowFacebook(),
    token: null,
    setRequiresSdk: () => {},
});

export function useFacebookContext() {
    const context = use(FacebookContext);
    return context;
}

export function useFacebookSdk() {
    const { sdk = null, setRequiresSdk } = useFacebookContext();
    useEffect(() => {
        setRequiresSdk(true);
    }, [setRequiresSdk]);
    return sdk;
}

export function useFacebookToken() {
    const { token = null } = useFacebookContext();
    return token;
}

interface FacebookContextProviderProps {
    appId?: string | null;
    locale?: string | null;
    version?: string;
    children: ReactNode;
}

export function FacebookContextProvider({
    appId = null,
    locale,
    version = 'v21.0',
    children,
}: FacebookContextProviderProps) {
    const isCheckingAuth = useIsCheckingAuth();
    const user = useUser();
    const [requiresSdk, setRequiresSdk] = useState(false);
    const [sdk, setSdk] = useState<facebook.FacebookStatic | null>(() => getWindowFacebook());
    const [token, setToken] = useState<string | null>(null);

    useEffect(() => {
        if (sdk !== null || isCheckingAuth || (user !== null && !requiresSdk)) {
            return () => {};
        }

        let canceled = false;
        loadFacebook({
            locale,
            version,
            appId,
        }).then((newSdk) => {
            if (!canceled) {
                setSdk(newSdk as facebook.FacebookStatic);
            }
        });
        return () => {
            canceled = true;
        };
    }, [sdk, appId, locale, version, user, isCheckingAuth, requiresSdk]);

    useEffect(() => {
        if (sdk === null) {
            return () => {};
        }
        function onStatusChange({ authResponse }: facebook.StatusResponse) {
            setToken(authResponse !== null ? authResponse.accessToken : null);
        }
        sdk.Event.subscribe('auth.statusChange', onStatusChange);
        return () => {
            sdk.Event.unsubscribe('auth.statusChange', onStatusChange);
        };
    }, [sdk]);

    // useEffect(() => {
    //     if (token !== null && sdk !== null && user === null) {
    //         console.log('LOGOUT');
    //         sdk.logout();
    //     }
    // }, [user, token, sdk]);

    const value = {
        appId,
        sdk,
        token,
        setRequiresSdk,
    };

    return <FacebookContext value={value}>{children}</FacebookContext>;
}
